import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";
import { ROUTES } from "@/lib/routes";

/**
 * Unified landscape CTA block (before footer) — same layout, type scale and alignment on all pages.
 */
export default function LandscapeCta({
  eyebrow,
  title,
  subtitle,
  buttonLabel,
  buttonTo = ROUTES.contact,
  testId = "landscape-cta",
  buttonTestId,
}) {
  return (
    <section className="relative bg-[#faf8f8] py-16 sm:py-20" data-testid={testId}>
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-[2rem] bg-[#3e2723] p-8 sm:rounded-[2.5rem] sm:p-10 lg:p-12"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 -right-24 h-[380px] w-[380px] rounded-full bg-[#fbd9e5]/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-0 h-[240px] w-[240px] rounded-full bg-[#fbd9e5]/10 blur-3xl"
          />

          <div className="relative grid items-center gap-6 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-8 xl:col-span-9">
              {eyebrow ? (
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#fbd9e5]/70 sm:text-[11px] sm:tracking-[0.3em]">
                  <Zap className="h-3.5 w-3.5" aria-hidden />
                  {eyebrow}
                </span>
              ) : null}
              <h2 className="mt-3 font-display text-[1.65rem] font-black leading-[1.08] tracking-tighter text-[#fbd9e5] sm:mt-4 sm:text-3xl lg:mt-3 lg:text-[2rem] lg:leading-[1.12] lg:whitespace-nowrap xl:text-[2.125rem]">
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-3 text-sm leading-relaxed text-white sm:text-base lg:mt-2.5 lg:text-[15px] lg:leading-snug lg:whitespace-nowrap xl:text-base">
                  {subtitle}
                </p>
              ) : null}
            </div>
            <div className="flex lg:col-span-4 lg:justify-end xl:col-span-3">
              <Link
                to={buttonTo}
                data-testid={buttonTestId || `${testId}-button`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#fbd9e5] px-7 py-3.5 text-sm font-semibold text-[#3e2723] shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-white sm:w-auto sm:px-8 sm:py-4 sm:text-[15px] lg:shrink-0"
              >
                {buttonLabel}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
